import ChatPlayground from "./ChatPlayground";
import TransitionView from "@components/shared/TransitionView";
import { useTranslation } from "react-i18next";
import {
  ChatUserAction,
  isMiddlePaneAction,
  isPaymentsAction,
  useNavigationState,
} from "@/zustand/NavigationState";
import { useNavigate } from "react-router-dom";
import { useMessageLoader } from "@/hooks/useMessageLoader";

import ThreadPlayground from "./ThreadPlayground";
import AttachmentViewerDialog from "./messages/attachmentViewer/AttachmentViewerDialog";
import { useThreadCommentsLoader } from "@/hooks/useThreadCommentsLoader";
import NewAIChatForm from "./NewAIChatForm";
import SubscriptionPlansView from "./SubscriptionPlansView";
import AddonCreditsView from "./AddonCreditsView";
import InvoicesView from "./InvoicesView";
import PaymentsFaqView from "./PaymentsFaqView";
import SettingsCategoryView from "./settings/SettingsCategoryView";

export default function MiddleColumn() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeChatId = useNavigationState((state) => state.activeChatId);
  const activeThreadId = useNavigationState((state) => state.activeThreadId);
  const activeChatUserAction = useNavigationState(
    (state) => state.activeChatUserAction,
  );

  const setChatUserAction = useNavigationState(
    (state) => state.setChatUserAction,
  );

  const cancelPaymentsView = useNavigationState(
    (state) => state.cancelPaymentsView,
  );

  // Load messages for the active chat
  useMessageLoader(activeChatId);

  // Load comments for the active thread
  useThreadCommentsLoader(activeChatId, activeThreadId);

  const isCreatingAIChat =
    activeChatUserAction === ChatUserAction.CREATE_AI_CHAT;
  const isViewingSubscription =
    activeChatUserAction === ChatUserAction.VIEW_SUBSCRIPTION;
  const isViewingAddonCredits =
    activeChatUserAction === ChatUserAction.VIEW_ADDON_CREDITS;
  const isViewingInvoices =
    activeChatUserAction === ChatUserAction.VIEW_INVOICES;
  const isViewingPaymentsFaq =
    activeChatUserAction === ChatUserAction.VIEW_PAYMENTS_FAQ;
  const isViewingAnyPayments = isPaymentsAction(activeChatUserAction);
  // isMiddlePaneAction is flag-gated for the settings term, so a persisted
  // VIEW_SETTINGS_CATEGORY in a flag-off build falls through to the empty
  // state (and route sync then clears it) — never the new UI.
  const isViewingSettingsCategory =
    isMiddlePaneAction(activeChatUserAction) &&
    activeChatUserAction === ChatUserAction.VIEW_SETTINGS_CATEGORY;
  const isViewingAnyMiddlePane = isMiddlePaneAction(activeChatUserAction);

  if (!activeChatId && !isCreatingAIChat && !isViewingAnyMiddlePane) {
    return (
      <div className="flex size-full">
        <p className="m-auto">{t("selectAChatToStartMessaging")}</p>
      </div>
    );
  }

  return (
    <TransitionView
      id="MiddleColumn"
      onCloseThreshold={0.7}
      trigger={!!activeChatId || isCreatingAIChat || isViewingAnyMiddlePane}
      onClose={() => {
        if (isViewingAnyPayments) {
          cancelPaymentsView();
        } else {
          navigate("/", { replace: true });
          setChatUserAction(null);
        }
      }}
    >
      {isViewingSubscription && !activeChatId ? (
        <SubscriptionPlansView />
      ) : isViewingAddonCredits && !activeChatId ? (
        <AddonCreditsView />
      ) : isViewingInvoices && !activeChatId ? (
        <InvoicesView />
      ) : isViewingPaymentsFaq && !activeChatId ? (
        <PaymentsFaqView />
      ) : isViewingSettingsCategory && !activeChatId ? (
        <SettingsCategoryView />
      ) : isCreatingAIChat ? (
        // No `&& !activeChatId` here: voice recording temporarily sets activeChatId
        // while CREATE_AI_CHAT is still active — the form must stay mounted.
        <NewAIChatForm />
      ) : (
        <>
          <ChatPlayground key={activeChatId} />
          <ThreadPlayground key={activeThreadId} />
          {/* One viewer for the whole middle column. It's driven entirely by
              the global AttachmentViewerState store (no props), so a single
              mount serves opens from both the main chat and threads. Mounting
              it inside each playground meant two identical live instances
              whenever a thread was open — duplicate live query, fetch/decrypt,
              and prefetch for the same attachment. */}
          <AttachmentViewerDialog />
        </>
      )}
    </TransitionView>
  );
}
